'use client';

import { useState, type DragEvent } from 'react';
import type { Tab, TabGroup } from '../../../store/layoutStore';
import type { LayoutState } from '../../../store/layoutStoreTypes';
import {
  isTabMentionable,
  serializeTabMentionPayload,
  TERRA_TAB_MENTION_MIME,
} from '../../../utils/mainContentTabMentionUtils';

type UseGroupedTabDragAndDropOptions = {
  tabs: Tab[];
  allTabs: Tab[];
  tabGroups: TabGroup[];
  reorderTabs: LayoutState['reorderMainContentTabs'];
  addTabToGroup: LayoutState['addTabToGroup'];
  removeTabFromGroup: LayoutState['removeTabFromGroup'];
  reorderTabsInGroup: LayoutState['reorderTabsInGroup'];
  updateTabGroup: LayoutState['updateTabGroup'];
  reorderTabGroups: LayoutState['reorderTabGroups'];
};

export function useGroupedTabDragAndDrop({
  tabs,
  allTabs,
  tabGroups,
  reorderTabs,
  addTabToGroup,
  removeTabFromGroup,
  reorderTabsInGroup,
  updateTabGroup,
  reorderTabGroups,
}: UseGroupedTabDragAndDropOptions) {
  const [draggedTabIndex, setDraggedTabIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggedGroupIndex, setDraggedGroupIndex] = useState<number | null>(
    null,
  );
  const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(
    null,
  );

  const handleDragStart =
    ({ index, tab }: { index: number; tab: Tab }) =>
    (event: DragEvent) => {
      setDraggedTabIndex(index);
      event.dataTransfer.effectAllowed = 'copyMove';
      if (isTabMentionable(tab)) {
        event.dataTransfer.setData(
          TERRA_TAB_MENTION_MIME,
          serializeTabMentionPayload(tab),
        );
      }
    };

  const handleDragEnd = () => {
    setDraggedTabIndex(null);
    setDragOverIndex(null);
    setDragOverGroupIndex(null);
  };

  const handleDragOver = (index: number) => (event: DragEvent) => {
    event.preventDefault();
    if (draggedTabIndex !== null && index !== draggedTabIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (index: number) => (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (draggedTabIndex === null || draggedTabIndex === index) {
      setDraggedTabIndex(null);
      setDragOverIndex(null);
      return;
    }

    const draggedTab = tabs[draggedTabIndex];
    const targetTab = tabs[index];
    if (!draggedTab || !targetTab) {
      setDraggedTabIndex(null);
      setDragOverIndex(null);
      return;
    }

    if (targetTab.groupId) {
      const group = tabGroups.find(
        (candidate) => candidate.id === targetTab.groupId,
      );
      if (group) {
        const draggedIndexInGroup = group.tabIds.indexOf(draggedTab.id);
        const targetIndexInGroup = group.tabIds.indexOf(targetTab.id);
        const isMovingRightWithinGroup =
          draggedIndexInGroup >= 0 && draggedIndexInGroup < targetIndexInGroup;
        const withoutDragged = group.tabIds.filter(
          (tabId) => tabId !== draggedTab.id,
        );
        const targetPosition = withoutDragged.indexOf(targetTab.id);
        addTabToGroup(draggedTab.id, targetTab.groupId);
        if (targetPosition >= 0) {
          const fromIndex = withoutDragged.length;
          const toIndex = isMovingRightWithinGroup
            ? targetPosition + 1
            : targetPosition;
          if (fromIndex !== toIndex) {
            reorderTabsInGroup(targetTab.groupId, fromIndex, toIndex);
          }
        }
      }
    } else {
      if (draggedTab.groupId) {
        removeTabFromGroup(draggedTab.id);
      }
      const fromIndex = allTabs.findIndex((tab) => tab.id === draggedTab.id);
      const targetIndex = allTabs.findIndex((tab) => tab.id === targetTab.id);
      if (fromIndex >= 0 && targetIndex >= 0 && fromIndex !== targetIndex) {
        const toIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
        reorderTabs(fromIndex, toIndex);
      }
    }

    setDraggedTabIndex(null);
    setDragOverIndex(null);
  };

  const handleGroupDragStart = (index: number) => (event: DragEvent) => {
    setDraggedGroupIndex(index);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleGroupDragEnd = () => {
    setDraggedGroupIndex(null);
    setDragOverGroupIndex(null);
  };

  const handleGroupDragOver = (index: number) => (event: DragEvent) => {
    event.preventDefault();
    if (draggedTabIndex !== null) {
      setDragOverGroupIndex(index);
      return;
    }
    if (draggedGroupIndex !== null && index !== draggedGroupIndex) {
      setDragOverGroupIndex(index);
    }
  };

  const handleGroupDrop =
    ({ groupId, index }: { groupId: string; index: number }) =>
    (event: DragEvent) => {
      event.preventDefault();
      if (draggedTabIndex !== null) {
        const draggedTab = tabs[draggedTabIndex];
        if (draggedTab && draggedTab.groupId !== groupId) {
          addTabToGroup(draggedTab.id, groupId);
          const targetGroup = tabGroups.find((group) => group.id === groupId);
          if (targetGroup?.isCollapsed) {
            updateTabGroup(groupId, { isCollapsed: false });
          }
        }
        setDraggedTabIndex(null);
        setDragOverIndex(null);
        setDragOverGroupIndex(null);
        return;
      }
      if (draggedGroupIndex !== null && draggedGroupIndex !== index) {
        reorderTabGroups(draggedGroupIndex, index);
      }
      setDraggedGroupIndex(null);
      setDragOverGroupIndex(null);
    };

  return {
    draggedTabIndex,
    dragOverIndex,
    draggedGroupIndex,
    dragOverGroupIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
    handleGroupDragStart,
    handleGroupDragEnd,
    handleGroupDragOver,
    handleGroupDrop,
  };
}
